import React, { Component } from "react";
import {
  listFirmwarePackages,
  compareFirmwarePackagesByVersionDesc,
  makeCdnUrl,
  parseNewFirmwarePackageFilename,
  findSomanetProductById,
} from "motion-master-client";
import { brandId } from "./brand";
import { withRouter } from "./withRouter";

class FirmwarePackages extends Component {
  constructor(props) {
    super(props);

    const searchParams = new URLSearchParams(this.props.location.search);

    this.state = {
      packages: null,
      filteredPackages: null,
      isLoading: false,
      searchText: searchParams.get("search") || "",
      filterName: searchParams.get("name") || "",
      filterProductId: searchParams.get("firmwareId") || "",
      filterProtocol: searchParams.get("protocol") || "",
    };
  }

  protocolMap = {
    0: "EtherNet/IP - CiA 402",
    1: "EtherCAT",
    2: "PROFINET - CiA 402",
    3: "CAN",
    10: "EtherNet/IP - LXM",
  };

  getValidFilters({ packages, filterName, filterProductId, filterProtocol }) {
    const validNames = new Set(
      packages
        .map((pkg) => {
          try {
            return parseNewFirmwarePackageFilename(pkg).hardwareName.replace(
              /-/g,
              " "
            );
          } catch {
            return null;
          }
        })
        .filter(Boolean)
    );

    const validProductIds = new Set(
      packages
        .map((pkg) => {
          try {
            return parseNewFirmwarePackageFilename(pkg).productId.toString();
          } catch {
            return null;
          }
        })
        .filter(Boolean)
    );

    const validProtocols = new Set(
      packages
        .map((pkg) => {
          try {
            const parsed = parseNewFirmwarePackageFilename(pkg);
            return (parsed.fieldbusProtocol ?? 1).toString();
          } catch {
            return null;
          }
        })
        .filter((p) => p !== null && p !== undefined)
    );

    const updates = {};

    if (filterName && !validNames.has(filterName)) {
      updates.filterName = "";
    }
    if (filterProductId && !validProductIds.has(filterProductId)) {
      updates.filterProductId = "";
    }
    if (
      filterProtocol &&
      filterProtocol !== "all" &&
      !validProtocols.has(filterProtocol)
    ) {
      updates.filterProtocol = "";
    }

    return { validNames, validProductIds, validProtocols, updates };
  }

  componentDidMount() {
    this.setState({ isLoading: true });
    listFirmwarePackages(brandId, false, false).then((packages) => {
      packages.sort(compareFirmwarePackagesByVersionDesc);
      if (this.props.version) {
        packages = packages.filter((pkg) =>
          pkg.includes(`${this.props.version}.zip`)
        );
      }
      this.setState(
        {
          packages,
          isLoading: false,
        },
        () => {
          this.props.onPackageCountChange?.(packages.length);
          this.validateAndApplyFilters();
        }
      );
    });
  }

  validateAndApplyFilters = () => {
    const { packages, filterName, filterProductId, filterProtocol } =
      this.state;
    if (!packages) return;

    const { updates } = this.getValidFilters({
      packages,
      filterName,
      filterProductId,
      filterProtocol,
    });

    if (Object.keys(updates).length > 0) {
      this.setState(updates, () => this.applyFilters());
    } else {
      this.applyFilters();
    }
  };

  componentDidUpdate(prevProps) {
    if (prevProps.isActive !== this.props.isActive) {
      if (this.props.isActive) {
        this.updateUrlFromCurrentState();
      } else {
        this.props.navigate(
          { pathname: this.props.location.pathname, search: "" },
          { replace: true }
        );
      }
    }
  }

  updateUrlFromCurrentState() {
    const { searchText, filterName, filterProductId, filterProtocol } =
      this.state;
    const params = new URLSearchParams();

    if (searchText) params.set("search", searchText);
    if (filterName) params.set("name", filterName);
    if (filterProductId) params.set("firmwareId", filterProductId);
    if (filterProtocol && filterProtocol !== "all")
      params.set("protocol", filterProtocol);

    this.props.navigate(
      { pathname: this.props.location.pathname, search: params.toString() },
      { replace: true }
    );
  }

  updateFilters(updates) {
    this.setState(updates, () => {
      this.applyFilters();
    });
  }

  applyFilters() {
    const {
      searchText,
      filterName,
      filterProductId,
      filterProtocol,
      packages,
    } = this.state;
    if (!packages) return;

    const { validNames, validProductIds, validProtocols } =
      this.getValidFilters({
        packages,
        filterName,
        filterProductId,
        filterProtocol,
      });

    const effectiveFilterName =
      filterName && validNames.has(filterName) ? filterName : "";
    const effectiveFilterProductId =
      filterProductId && validProductIds.has(filterProductId)
        ? filterProductId
        : "";
    const effectiveFilterProtocol =
      filterProtocol &&
      filterProtocol !== "all" &&
      validProtocols.has(filterProtocol)
        ? filterProtocol
        : "";

    const params = new URLSearchParams();
    if (searchText) params.set("search", searchText);
    if (effectiveFilterName) params.set("name", effectiveFilterName);
    if (effectiveFilterProductId)
      params.set("firmwareId", effectiveFilterProductId);
    if (effectiveFilterProtocol)
      params.set("protocol", effectiveFilterProtocol);

    this.props.navigate(
      { pathname: this.props.location.pathname, search: params.toString() },
      { replace: true }
    );

    const normalize = (str) => str.toLowerCase().replace(/[-_]/g, " ");
    const normalizedSearch = normalize(searchText || "");

    const filteredPackages = packages.filter((pkg) => {
      let parsed;
      try {
        parsed = parseNewFirmwarePackageFilename(pkg);
      } catch {
        return false;
      }

      const hardwareName = normalize(parsed.hardwareName);
      const productName = normalize(
        findSomanetProductById(parsed.productId)?.name || ""
      );

      if (
        effectiveFilterName &&
        hardwareName !== normalize(effectiveFilterName)
      )
        return false;
      if (
        effectiveFilterProductId &&
        parsed.productId.toString() !== effectiveFilterProductId
      )
        return false;
      if (effectiveFilterProtocol) {
        const protocolValue = parsed.fieldbusProtocol?.toString() ?? "1";
        if (effectiveFilterProtocol !== protocolValue) return false;
      }

      if (normalizedSearch) {
        const searchableString = `${hardwareName} ${productName} ${
          parsed.productId
        } ${parsed.productVersion} ${parsed.keyId} ${
          parsed.fieldbusProtocol != null
            ? this.protocolMap[parsed.fieldbusProtocol]
            : ""
        } ${normalize(pkg)}`;
        if (!searchableString.includes(normalizedSearch)) return false;
      }

      return true;
    });

    this.setState({ filteredPackages });
  }

  handleSearch(event) {
    this.updateFilters({ searchText: event.target.value });
  }

  clearSearch() {
    this.updateFilters({ searchText: "" });
  }

  render() {
    return (
      <div className="d-flex flex-column gap-3">
        <div className="d-flex">
          <input
            type="text"
            className="form-control"
            placeholder="Search by name and firmware ID..."
            value={this.state.searchText}
            onChange={(e) => this.handleSearch(e)}
          />
          {this.state.searchText && (
            <button
              className="btn ms-2"
              onClick={() => this.clearSearch()}
              title="Clear the input"
            >
              <span aria-hidden="true">×</span>
            </button>
          )}
        </div>

        {this.state.filteredPackages === null ? (
          <strong>Loading...</strong>
        ) : (
          <div
            className="overflow-y-auto"
            style={{ maxHeight: "calc(100vh - 468px)" }}
          >
            <table
              className="table table-hover w-100"
              style={{ tableLayout: "fixed" }}
            >
              <thead>
                <tr>
                  <th style={{ width: "41%" }}>Name</th>
                  <th style={{ width: "10%", whiteSpace: "nowrap" }}>
                    Firmware ID
                  </th>
                  <th
                    className="text-end"
                    style={{ width: "7%", whiteSpace: "nowrap" }}
                    title="Revision"
                  >
                    Rev.
                  </th>
                  <th
                    className="text-end"
                    style={{ width: "7%", whiteSpace: "nowrap" }}
                    title="Encryption Key"
                  >
                    Enc. Key
                  </th>
                  <th style={{ width: "21%", whiteSpace: "nowrap" }}>
                    Protocol
                  </th>
                  <th style={{ width: "14%", textAlign: "right" }}>Actions</th>
                </tr>
                <tr>
                  <th>
                    <select
                      className="form-select"
                      value={this.state.filterName}
                      onChange={(e) =>
                        this.updateFilters({
                          filterName: e.target.value,
                          filterProductId: "",
                        })
                      }
                    >
                      <option value="">All</option>
                      {[
                        ...new Set(
                          (this.state.packages || [])
                            .map((pkg) => {
                              try {
                                const parsed =
                                  parseNewFirmwarePackageFilename(pkg);

                                return parsed.hardwareName.replace(/-/g, " ");
                              } catch {
                                return null;
                              }
                            })
                            .filter(Boolean)
                        ),
                      ].map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </th>

                  <th>
                    <select
                      className="form-select"
                      value={this.state.filterProductId}
                      onChange={(e) =>
                        this.updateFilters({
                          filterProductId: e.target.value,
                        })
                      }
                    >
                      <option value="">All</option>
                      {[
                        ...new Set(
                          (this.state.packages || [])
                            .filter((pkg) => {
                              if (!this.state.filterName) return true;
                              try {
                                const parsed =
                                  parseNewFirmwarePackageFilename(pkg);
                                return (
                                  parsed.hardwareName.replace(/-/g, " ") ===
                                  this.state.filterName
                                );
                              } catch {
                                return false;
                              }
                            })
                            .map((pkg) => {
                              try {
                                return parseNewFirmwarePackageFilename(pkg)
                                  .productId;
                              } catch {
                                return null;
                              }
                            })
                            .filter(Boolean)
                        ),
                      ]
                        .sort((a, b) => a - b)
                        .map((pid) => (
                          <option key={pid} value={pid}>
                            {pid}
                          </option>
                        ))}
                    </select>
                  </th>

                  <th colSpan={2}>&nbsp;</th>

                  <th>
                    <select
                      className="form-select"
                      value={this.state.filterProtocol || "all"}
                      onChange={(e) =>
                        this.updateFilters({ filterProtocol: e.target.value })
                      }
                    >
                      <option value="all">All</option>
                      {[
                        ...new Set(
                          (this.state.packages || [])
                            .map((pkg) => {
                              try {
                                const parsed =
                                  parseNewFirmwarePackageFilename(pkg);
                                return parsed.fieldbusProtocol ?? 1;
                              } catch {
                                return null;
                              }
                            })
                            .filter((p) => p !== null && p !== undefined)
                        ),
                      ].map((protocol) => {
                        const label = this.protocolMap[protocol] ?? protocol;
                        return (
                          <option key={protocol} value={protocol}>
                            {label}
                          </option>
                        );
                      })}
                    </select>
                  </th>

                  <th>&nbsp;</th>
                </tr>
              </thead>
              {(() => {
                const rows = this.state.filteredPackages
                  .map((item) => {
                    let parsed;
                    try {
                      parsed = parseNewFirmwarePackageFilename(item);
                    } catch {
                      return null;
                    }

                    const link = makeCdnUrl(
                      `/brands/${brandId}/packages/${encodeURIComponent(item)}`
                    );

                    const somanetProductName = findSomanetProductById(
                      parsed.productId
                    );

                    return (
                      <React.Fragment key={item}>
                        <tbody>
                          <tr>
                            <td>
                              <div className="fw-bold">
                                {parsed.hardwareName.replace(/-/g, " ")}
                              </div>
                              {somanetProductName?.name && (
                                <>
                                  <div
                                    title={somanetProductName.name}
                                    className="text-small text-muted text-truncate"
                                  >
                                    {somanetProductName.name}
                                  </div>
                                  {/* <div
                                    title={item}
                                    className="fs-6 text-muted opacity-75 text-truncate"
                                  >
                                    {item}
                                  </div> */}
                                </>
                              )}
                            </td>
                            <td className="text-end">{parsed.productId}</td>
                            <td className="text-end">
                              {parsed.productVersion}
                            </td>
                            <td className="text-end">{parsed.keyId}</td>
                            <td>
                              {parsed.fieldbusProtocol != null
                                ? this.protocolMap[parsed.fieldbusProtocol] ||
                                  "Unknown"
                                : "EtherCAT"}
                            </td>
                            <td className="text-end">
                              <a
                                href={link}
                                className="btn btn-sm btn-primary"
                                title={`Download ${item}`}
                              >
                                Download (.zip)
                              </a>
                            </td>
                          </tr>
                        </tbody>
                      </React.Fragment>
                    );
                  })
                  .filter(Boolean);

                // Conditional rendering for empty results
                if (rows.length === 0) {
                  return (
                    <thead>
                      <tr>
                        <td colSpan={6}>
                          <div className="alert alert-warning m-0" role="alert">
                            <strong>No firmware packages found</strong>
                          </div>
                        </td>
                      </tr>
                    </thead>
                  );
                }

                return rows;
              })()}
            </table>
          </div>
        )}
      </div>
    );
  }
}

export default withRouter(FirmwarePackages);
